import React, { useEffect, useState } from "react";
import { CheckCircleOutlined, CreditCardOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';

const AadharSuccessPage = () => {
  const [aadhaarData, setAadhaarData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const params = new URL(window.location.href).searchParams;
      const encodedData = params.get("data");

      if (!encodedData) {
        setError("No Aadhaar data provided in the URL.");
        return;
      }

      const decodedData = decodeURIComponent(encodedData);
      const jsonData = JSON.parse(decodedData);
      setAadhaarData(jsonData);
    } catch (error) {
      console.error("Error decoding Aadhaar data:", error);
      setError("Failed to load Aadhaar data. Please try again or contact support.");
    }
  }, []);

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #eff6ff, #f3f4f6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{
            color: '#dc2626',
            fontSize: '1.125rem',
            fontWeight: '500'
          }}>{error}</p>
          <button
            onClick={() => window.history.back()}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.75rem',
              background: 'linear-gradient(to right, #1e40af, #1e3a8a)',
              color: 'white',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = 'linear-gradient(to right, #1e3a8a, #1d4ed8)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = 'linear-gradient(to right, #1e40af, #1e3a8a)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!aadhaarData) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #eff6ff, #f3f4f6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            width: '3rem',
            height: '3rem',
            border: '4px solid #2563eb',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            marginTop: '1rem',
            color: '#4b5563',
            fontSize: '1.125rem'
          }}>Loading Aadhaar data...</p>
        </div>
      </div>
    );
  }

  const issuedTo = aadhaarData?.resp?.result?.[0]?.parsedFile?.data?.issuedTo || {};
  const address = issuedTo?.address || {};

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #eff6ff, #f3f4f6)'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(to right, #1e3a8a, #1e40af)',
        color: 'white',
        padding: '2rem 1.5rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <p style={{
          fontSize: '0.875rem',
          fontWeight: '500',
          marginBottom: '0.5rem',
          opacity: '0.9'
        }}>Verification Complete</p>
        <h1 style={{
          fontSize: '1.875rem',
          fontWeight: '600'
        }}>Aadhaar Verification</h1>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '80rem',
        margin: '0 auto',
        padding: '2rem 1rem'
      }}>
        {/* Success Banner */}
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          border: '1px solid #e5e7eb',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{
              background: '#f0fdf4',
              borderRadius: '50%',
              padding: '0.75rem'
            }}>
              <CheckCircleOutlined style={{
                fontSize: '2.5rem',
                color: '#22c55e'
              }} />
            </div>
            <div>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#1f2937'
              }}>Verification Successful!</h2>
              <p style={{
                color: '#4b5563',
                marginTop: '0.25rem'
              }}>Your Aadhaar details have been verified successfully</p>
            </div>
          </div>
        </div>

        {/* Personal Details Card */}
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          border: '1px solid #e5e7eb',
          padding: '2rem',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <CreditCardOutlined style={{
              fontSize: '1.5rem',
              color: '#1e40af'
            }} />
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#1f2937'
            }}>Personal Information</h3>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth >= 768 ? '1fr 1fr' : '1fr',
            gap: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <UserOutlined style={{
                fontSize: '1.25rem',
                color: '#2563eb',
                marginTop: '0.25rem',
                flexShrink: 0
              }} />
              <div>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}>Full Name</p>
                <p style={{
                  fontSize: '1.125rem',
                  fontWeight: '500',
                  color: '#1f2937'
                }}>{issuedTo.name || "N/A"}</p>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <UserOutlined style={{
                fontSize: '1.25rem',
                color: '#2563eb',
                marginTop: '0.25rem',
                flexShrink: 0
              }} />
              <div>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}>Gender</p>
                <p style={{
                  fontSize: '1.125rem',
                  fontWeight: '500',
                  color: '#1f2937'
                }}>{issuedTo.gender || "N/A"}</p>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <CalendarOutlined style={{
                fontSize: '1.25rem',
                color: '#2563eb',
                marginTop: '0.25rem',
                flexShrink: 0
              }} />
              <div>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}>Date of Birth</p>
                <p style={{
                  fontSize: '1.125rem',
                  fontWeight: '500',
                  color: '#1f2937'
                }}>{issuedTo.dob || "N/A"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={() => window.print()}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.75rem',
              background: '#f3f4f6',
              color: '#4b5563',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.background = '#e5e7eb'}
            onMouseOut={e => e.currentTarget.style.background = '#f3f4f6'}
          >
            Print Details
          </button>
          <button
            onClick={() => {
              window.history.back();
              window.close();
            }}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '0.75rem',
              background: 'linear-gradient(to right, #1e40af, #1e3a8a)',
              color: 'white',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = 'linear-gradient(to right, #1e3a8a, #1d4ed8)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = 'linear-gradient(to right, #1e40af, #1e3a8a)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            Back to Application
          </button>
        </div>
      </div>
    </div>
  );
};

export default AadharSuccessPage;